import { FunctionComponent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import i18next from 'i18next';
import { MailConfirmation } from '@hub/components/mail-confirmation-nova/mail-confirmation';
import { getDialogBlurStyles } from '@hub/features/contest-category/dialogs/shared/dialog-blur.styles';
import { EnvService } from '@hub/services/core/env-service';
import { styled } from '@mui/material';
import { trackMixpanel } from '@monorepo/client-common';
import { useGetFeatureFlag } from '@monorepo/feature-flag/client';
import {
  Button,
  Dialog,
  DialogHandle,
  Stack,
  Tooltip,
  useBreakpoints,
} from '@monorepo/nova-ui';
import { LoadingState, useLoading } from '@monorepo/react-hooks';
import {
  Account,
  EventTypes,
  MailOtpSource,
  PayoutSource,
  WithdrawBlockReason,
  WithdrawRulesType,
} from '@monorepo/types';
import { useStore } from '../../helpers/use-store';
import { WithdrawalConfirmationModal } from '../dashboard-templates/dashboard-common-new/withdrawal-confirmation-modal';
import { PayoutRequestForm } from './payout-request-form';

interface Props {
  maxWithdrawal: number;
  minWithdrawal: number;
  credits: number;
  userPayoutPercentage: number;
  reason?: WithdrawBlockReason;
  showAmountSelection?: boolean;
  disabled?: boolean;
  sourceId: string;
  displayName: string;
  tsAccountId?: string;
  source: PayoutSource;
  validateWithDrawEligible: () => Promise<void>;
  className?: string;
  account?: Account;
}

const createTooltip = (
  reason: WithdrawBlockReason,
  availableAmount: number,
) => {
  // Priority rule: If balance < $150, show balance tooltip even if cooldown is active
  const isMinimumAmountRule = reason?.rule === WithdrawRulesType.MinimumAmount;
  const isCooldownRule =
    reason?.rule === WithdrawRulesType.MinimumPayoutCoolDown;

  // If minimum amount rule exists and balance < $150, prioritize it
  if (isMinimumAmountRule && availableAmount < 150) {
    return i18next.t('withdrawFundsNova:tooltip.available_after_minimum', {
      amount: 150,
    });
  }

  // If cooldown is active and balance >= $150, show cooldown message
  if (isCooldownRule && reason?.currentValue !== undefined) {
    const daysRemaining = Math.max(
      0,
      (reason.ruleValue as number) - (reason.currentValue as number),
    );
    if (daysRemaining > 0) {
      return i18next.t('withdrawFundsNova:tooltip.cooldown_active', {
        days: daysRemaining,
      });
    }
  }

  // Fallback to original logic for other rules
  const RuleToText = {
    [WithdrawRulesType.MinimumAmount]: i18next.t(
      'withdrawFundsNova:tooltip.minimum_amount',
      {
        ruleValue: reason?.ruleValue,
        currentValue: reason?.currentValue?.toFixed(2),
      },
    ),
    [WithdrawRulesType.MinimumPayoutCoolDown]: reason?.currentValue
      ? i18next.t('withdrawFundsNova:tooltip.minimum_payout_current_value', {
          currentValue: reason?.currentValue,
          ruleValue: reason?.ruleValue,
        })
      : i18next.t('withdrawFundsNova:tooltip.minimum_payout'),
    [WithdrawRulesType.MinimumBalance]: i18next.t(
      'withdrawFundsNova:tooltip.minimum_balance',
      {
        currentValue: reason?.currentValue?.toFixed(2),
        ruleValue: reason?.ruleValue,
      },
    ),
    [WithdrawRulesType.MinimumVesting]: i18next.t(
      'withdrawFundsNova:tooltip.minimum_vesting',
      {
        ruleValue: reason?.ruleValue,
        currentValue: reason?.currentValue,
      },
    ),
    [WithdrawRulesType.OpenPositions]: i18next.t(
      'withdrawFundsNova:tooltip.open_positions',
    ),
    [WithdrawRulesType.WithdrawTarget]: i18next.t(
      'withdrawFundsNova:tooltip.withdraw_target',
      {
        ruleValue: reason?.ruleValue,
        currentValue: reason?.currentValue?.toFixed(2),
      },
    ),
  } satisfies Record<WithdrawRulesType, string>;

  return reason?.rule != null ? RuleToText[reason.rule] : '';
};

const StyledWithdrawDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    position: 'relative',
    overflow: 'visible',
    maxWidth: theme.spacing(163),
    width: '100%',
    ...getDialogBlurStyles(theme, { smallBlur: true }),
    '& > .MuiBox-root': {
      width: '100%',
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(10, 6),
      },
    },
  },
  '& .MuiIconButton-root[aria-label="Close"] svg': {
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.pxToRem(16),
    },
  },
}));

const WithdrawFundsContent: FunctionComponent<Props> = ({
  disabled,
  reason,
  displayName,
  tsAccountId,
  validateWithDrawEligible,
  source,
  showAmountSelection,
  maxWithdrawal,
  sourceId,
  userPayoutPercentage,
  credits,
  account,
}) => {
  const { isLoading, updateLoadingState } = useLoading();
  const { isMobile } = useBreakpoints();
  const { t } = useTranslation([
    'mailConfirmationNova',
    'common',
    'errors',
    'withdrawFundsNova',
  ]);

  const {
    dataStore: { accountStore, userStore },
  } = useStore();

  const [showConfirmation, setShowConfirmation] = useState(false);

  const withdrawalConfirmationModalEnabled = useGetFeatureFlag(
    'enable-revert-withdrawal-mechanism',
    false,
    {
      env: EnvService.application,
    },
  );

  const reasonTooltip = reason ? createTooltip(reason, maxWithdrawal) : '';

  const handleWithdrawClick = async () => {
    if (source === PayoutSource.Account && tsAccountId) {
      try {
        updateLoadingState(LoadingState.Loading);
        await accountStore.sync(tsAccountId, true);
        await validateWithDrawEligible();
        updateLoadingState(LoadingState.Loaded);
      } catch (e) {
        console.error('Failed to sync account', e);
        updateLoadingState(LoadingState.Error);
        message.error(t('errors:error_refresh'), 3);

        return;
      }
    }

    if (withdrawalConfirmationModalEnabled) {
      setShowConfirmation(true);
    } else {
      handleConfirmWithdrawal();
    }
  };

  const handleConfirmWithdrawal = () => {
    setShowConfirmation(false);

    trackMixpanel({
      event: EventTypes.WITHDRAW_MODAL_OPENED,
      properties: {
        email: userStore.currentUser.email,
        platform: EnvService.get().application,
        programId: account?.programId,
        orderId: account?.purchaseId,
      },
    });

    modalRef.current?.open();
  };

  const modalRef = useRef<DialogHandle>(null);

  return (
    <>
      {withdrawalConfirmationModalEnabled && (
        <WithdrawalConfirmationModal
          isOpen={showConfirmation}
          onClose={() => setShowConfirmation(false)}
          onConfirm={handleConfirmWithdrawal}
          isLoading={isLoading}
        />
      )}

      <StyledWithdrawDialog ref={modalRef} maxWidth={'sm'}>
        <MailConfirmation
          verifiedCodeComponent={
            <PayoutRequestForm
              maxWithdrawal={maxWithdrawal}
              showAmountSelection={showAmountSelection}
              source={source}
              sourceId={sourceId}
              modalRef={modalRef}
              displayName={displayName}
              credits={credits}
              userPayoutPercentage={userPayoutPercentage}
              validateWithDrawEligible={validateWithDrawEligible}
              isAffiliate={source === PayoutSource.Affiliate}
            />
          }
          sendCodeStageHeader={t('mailConfirmationNova:send_code_header')}
          sendCodeStageMessage={t('mailConfirmationNova:send_code_message')}
          source={source as unknown as MailOtpSource}
          sourceId={sourceId}
          displayName={displayName}
        />
      </StyledWithdrawDialog>
      <Stack direction={'row'}>
        <Tooltip title={reasonTooltip}>
          <Button
            variant={'outlined'}
            disabled={disabled}
            onClick={handleWithdrawClick}
            loading={isLoading}
            size={isMobile ? 'sm' : 'md'}
          >
            {t('common:withdraw')}
          </Button>
        </Tooltip>
      </Stack>
    </>
  );
};

export default WithdrawFundsContent;
